import { motion } from "framer-motion";
import { ShieldCheck, Clock4, Sparkles, FileCheck2, Star } from "lucide-react";

const pillars = [
  {
    icon: ShieldCheck,
    title: "Garantie Décennale & RC Pro",
    detail:
      "Assurance décennale française en cours de validité sur tous nos chantiers. Attestation fournie avec chaque devis.",
    testId: "trust-card-decennale",
  },
  {
    icon: Clock4,
    title: "Respect Strict des Délais",
    detail:
      "Un planning d'intervention précis, daté et contractuel. Pas de chantier abandonné ni de semaine d'absence inexpliquée.",
    testId: "trust-card-delais",
  },
  {
    icon: Sparkles,
    title: "Propreté & Bâchage Intégral",
    detail:
      "Protection minutieuse de vos sols, meubles et passages dès le premier jour. Remise des clés dans un intérieur prêt à vivre.",
    testId: "trust-card-proprete",
  },
  {
    icon: FileCheck2,
    title: "Devis Détaillé sous Huitaine",
    detail:
      "Visite technique gratuite sur rendez-vous, puis devis détaillé sous huitaine : métrés poste par poste, sans mauvaise surprise ni frais cachés.",
    testId: "trust-card-devis",
  },
];

const testimonials = [
  {
    quote:
      "De bon conseil et travail de qualité pour une très grosse rénovation d'un rez-de-chaussée complet : inversion des pièces, isolation performante, placo et peinture, sol en parquet, bibliothèque et placard d'entrée sur mesure, pose de cuisine. Je recommande !",
    author: "Hélène A.",
    location: "Rénovation complète d'un rez-de-chaussée — avis Google",
  },
  {
    quote:
      "Très belle rénovation de la devanture du Tabac de Montfort. Respect des délais et du devis. Un excellent professionnel. Je recommande fortement.",
    author: "TheCj51",
    location: "Rénovation de devanture de commerce — avis Google",
  },
  {
    quote:
      "Suite à l'acquisition d'un bien dans son jus voire complètement délabré, j'ai fait appel à Louis Larsson. Il a effectué l'entière rénovation : isolation, pose de fenêtres, faux plafonds, électricité, plomberie… délais et devis respectés ! Rassurant, de bons conseils et toujours à l'écoute.",
    author: "Cecilia G.",
    location: "Rénovation intégrale d'une maison ancienne — avis Google",
  },
];

export const TrustMatrix = () => {
  return (
    <section id="confiance" className="py-28 lg:py-36" data-testid="trust-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-16"
        >
          <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#9E5A38] mb-5">
            Le contrat de confiance immédiat
          </p>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight leading-[1.15] text-[#1C1815]">
            Votre tranquillité, signée noir sur blanc
          </h2>
          <a
            href="https://www.google.com/maps/search/?api=1&query=Louis+Larsson+Peinture+et+R%C3%A9novation+Les+Mesnuls"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-link"
            className="mt-6 inline-flex items-center gap-3 bg-[#FFFFFF] border border-[#1A1714]/10 rounded-full px-6 py-3 hover:border-[#9E5A38] transition-colors duration-300"
          >
            <span className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, s) => (
                <Star key={s} className="w-4 h-4 fill-[#BFA574] text-[#BFA574]" />
              ))}
            </span>
            <span className="text-sm font-semibold text-[#1A1714]">4,8/5</span>
            <span className="text-sm text-[#8A7E72]">26 avis Google vérifiés</span>
          </a>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-24">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 }}
              className="group bg-[#FFFFFF] border border-[#1A1714]/10 rounded-sm p-8 hover:border-[#9E5A38]/50 hover:-translate-y-1 transition-all duration-500"
              data-testid={p.testId}
            >
              <p.icon className="w-7 h-7 text-[#9E5A38] mb-6" strokeWidth={1.5} />
              <h3 className="font-serif-display text-xl font-medium text-[#1C1815] leading-snug">{p.title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-[#4A433D]">{p.detail}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <motion.blockquote
              key={t.author}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.12 }}
              className="bg-[#F3EFE8] border border-[#1A1714]/10 rounded-sm p-8 flex flex-col"
              data-testid={`testimonial-card-${i}`}
            >
              <div className="flex gap-1 mb-5">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} className="w-3.5 h-3.5 fill-[#BFA574] text-[#BFA574]" />
                ))}
              </div>
              <p className="font-serif-display italic text-lg leading-relaxed text-[#1C1815] flex-1">
                « {t.quote} »
              </p>
              <footer className="mt-6 pt-5 border-t border-[#1A1714]/10">
                <p className="text-sm font-semibold text-[#1A1714]">{t.author}</p>
                <p className="font-mono-tech text-[11px] text-[#8A7E72] mt-1">{t.location}</p>
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </div>
    </section>
  );
};
